import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/marketing/SectionHeading";
import { useTilt, Glare } from "@/components/marketing/Depth4D";
import { PanelAmbience } from "@/components/marketing/HeroCinematic";
import { SLIDES } from "./showcaseSlides";

const DURATION = 6500;
const EASE = [0.16, 1, 0.3, 1];

const Dots = ({ idx, setIdx, running }) => (
  <div className="flex items-center gap-2.5" data-testid="booking-showcase-dots">
    {SLIDES.map((s, i) => (
      <button key={s.id} type="button" onClick={() => setIdx(i)} aria-label={`${i + 1}`} data-testid={`booking-showcase-dot-${i}`}
        className="relative h-[3px] rounded-full overflow-hidden bg-white/15 transition-[width] duration-500" style={{ width: i === idx ? 56 : 18 }}>
        {i === idx && (
          <motion.span key={`${idx}-${running}`} className="absolute inset-y-0 left-0 bg-[#D4AF37]"
            initial={{ width: "0%" }} animate={{ width: running ? "100%" : "100%" }} transition={{ duration: running ? DURATION / 1000 : 0, ease: "linear" }} />
        )}
      </button>
    ))}
  </div>
);

// Reservations: a self-playing cinematic slideshow of how a real ARI booking flows.
export const BookingShowcase = () => {
  const { t } = useLang();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-20% 0px -20% 0px" });
  const [idx, setIdx] = useState(0);
  const tilt = useTilt(2);
  const slide = SLIDES[idx];
  const Visual = slide.Visual;

  useEffect(() => {
    if (!inView) return;
    const id = setTimeout(() => setIdx((i) => (i + 1) % SLIDES.length), DURATION);
    return () => clearTimeout(id);
  }, [idx, inView]);

  const enter = reduce ? { opacity: 0 } : { opacity: 0, y: 18, scale: 0.985 };
  const exit = reduce ? { opacity: 0, transition: { duration: 0.3 } } : { opacity: 0, y: -12, transition: { duration: 0.35, ease: EASE } };

  return (
    <section ref={ref} className="relative max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24" data-testid="booking-showcase">
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-10 sm:mb-12">
        <SectionHeading className="lg:col-span-7" testId="booking-heading" eyebrow="Reservations" lead="From booking to table." accent="ARI handles every step." />
        <p className="lg:col-span-5 text-white/65 text-base leading-relaxed">
          {t("Here is what happens when a guest books with a venue that runs on ARI, from choosing a time to your team seeing it live.")}
        </p>
      </div>

      <motion.div className="group relative rounded-[28px] overflow-hidden" style={tilt.style} {...tilt.handlers} data-testid="booking-showcase-stage">
        <PanelAmbience testId="booking-ambience" network={12} />
        <div aria-hidden className="absolute inset-0 z-[1] rounded-[28px] pointer-events-none" style={{ border: "1px solid rgba(212,175,55,0.2)" }} />
        <div aria-hidden className="absolute inset-x-0 top-0 z-[1] h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(246,227,166,0.6), transparent)" }} />
        <Glare glare={tilt.glare} />
        <div className="relative z-[2] grid lg:grid-cols-12 gap-8 lg:gap-12 p-6 sm:p-10 lg:p-14 min-h-[460px] items-center">
          <div className="lg:col-span-5">
            <AnimatePresence mode="wait">
              <motion.div key={slide.id} initial={enter} animate={{ opacity: 1, y: 0, scale: 1 }} exit={exit} transition={{ duration: 0.7, ease: EASE }} data-testid={`booking-slide-${slide.id}`}>
                <p className="font-mono-lux text-[12px] tracking-[0.3em] text-[#D4AF37]" data-no-translate>0{idx + 1} / 0{SLIDES.length}</p>
                <h3 className="font-serif-lux text-3xl sm:text-4xl text-[#F5EFE2] mt-4 leading-tight" data-testid="booking-slide-title">{t(slide.title)}</h3>
                <p className="text-white/65 text-[15px] leading-relaxed mt-4 max-w-md" data-testid="booking-slide-caption">{t(slide.caption)}</p>
              </motion.div>
            </AnimatePresence>
            <div className="mt-10"><Dots idx={idx} setIdx={setIdx} running={inView} /></div>
          </div>
          <div className="lg:col-span-7" style={{ perspective: 1400 }}>
            <AnimatePresence mode="wait">
              <motion.div key={slide.id} initial={reduce ? { opacity: 0 } : { opacity: 0, rotateY: -8, z: -80, x: 30 }}
                animate={{ opacity: 1, rotateY: 0, z: 0, x: 0 }}
                exit={reduce ? { opacity: 0, transition: { duration: 0.3 } } : { opacity: 0, rotateY: 6, z: -60, x: -24, transition: { duration: 0.35, ease: EASE } }}
                transition={{ duration: 0.9, ease: EASE }} style={{ transformStyle: "preserve-3d" }} data-testid="booking-slide-visual">
                <Visual />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
      <p className="mt-4 text-[11.5px] text-white/40" data-testid="booking-disclaimer">{t("Illustration with sample names. The rules shown are ARI's real defaults; each venue can change them.")}</p>
    </section>
  );
};

export default BookingShowcase;
